/**
 * Camunda 7.18 Message and Signal REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/message/
 * https://docs.camunda.org/manual/7.18/reference/rest/signal/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  CorrelationMessageDto,
  MessageCorrelationResult,
  SignalDto,
  Variables,
} from '../types';

export class MessageSignalService {
  private client: CamundaApiClient;

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Message Correlation
  // ============================================================================

  /**
   * Correlate a message to the process engine
   */
  async correlateMessage(dto: CorrelationMessageDto): Promise<MessageCorrelationResult[]> {
    return this.client.post<MessageCorrelationResult[]>('/engine-rest/message', dto);
  }

  /**
   * Correlate a message and start a process instance
   */
  async correlateMessageAndStartProcess(options: {
    messageName: string;
    businessKey?: string;
    tenantId?: string;
    withoutTenantId?: boolean;
    processVariables?: Variables;
    correlationKeys?: Variables;
    resultEnabled?: boolean;
    variablesInResultEnabled?: boolean;
  }): Promise<MessageCorrelationResult[]> {
    return this.correlateMessage({
      messageName: options.messageName,
      businessKey: options.businessKey,
      tenantId: options.tenantId,
      withoutTenantId: options.withoutTenantId,
      processVariables: options.processVariables,
      correlationKeys: options.correlationKeys,
      resultEnabled: options.resultEnabled ?? true,
      variablesInResultEnabled: options.variablesInResultEnabled,
    });
  }

  /**
   * Correlate a message to a specific process instance
   */
  async correlateMessageToProcessInstance(options: {
    messageName: string;
    processInstanceId: string;
    processVariables?: Variables;
    correlationKeys?: Variables;
    resultEnabled?: boolean;
    variablesInResultEnabled?: boolean;
  }): Promise<MessageCorrelationResult[]> {
    return this.correlateMessage({
      messageName: options.messageName,
      processInstanceId: options.processInstanceId,
      processVariables: options.processVariables,
      correlationKeys: options.correlationKeys,
      resultEnabled: options.resultEnabled ?? true,
      variablesInResultEnabled: options.variablesInResultEnabled,
    });
  }

  /**
   * Correlate a message to all matching executions
   */
  async correlateMessageToAll(options: {
    messageName: string;
    businessKey?: string;
    tenantId?: string;
    withoutTenantId?: boolean;
    processVariables?: Variables;
    correlationKeys?: Variables;
    localCorrelationKeys?: Variables;
    resultEnabled?: boolean;
    variablesInResultEnabled?: boolean;
  }): Promise<MessageCorrelationResult[]> {
    return this.correlateMessage({
      messageName: options.messageName,
      businessKey: options.businessKey,
      tenantId: options.tenantId,
      withoutTenantId: options.withoutTenantId,
      processVariables: options.processVariables,
      correlationKeys: options.correlationKeys,
      localCorrelationKeys: options.localCorrelationKeys,
      all: true,
      resultEnabled: options.resultEnabled ?? true,
      variablesInResultEnabled: options.variablesInResultEnabled,
    });
  }

  // ============================================================================
  // Signal
  // ============================================================================

  /**
   * Throw a signal event
   */
  async throwSignal(dto: SignalDto): Promise<void> {
    await this.client.post<void>('/engine-rest/signal', dto);
  }

  /**
   * Throw a signal to all matching executions
   */
  async throwSignalToAll(options: {
    name: string;
    variables?: Variables;
    tenantId?: string;
    withoutTenantId?: boolean;
  }): Promise<void> {
    await this.throwSignal({
      name: options.name,
      variables: options.variables,
      tenantId: options.tenantId,
      withoutTenantId: options.withoutTenantId,
    });
  }

  /**
   * Throw a signal to a specific execution
   */
  async throwSignalToExecution(options: {
    name: string;
    executionId: string;
    variables?: Variables;
  }): Promise<void> {
    await this.throwSignal({
      name: options.name,
      executionId: options.executionId,
      variables: options.variables,
    });
  }
}

// Singleton instance
let messageSignalServiceInstance: MessageSignalService | null = null;

export function getMessageSignalService(): MessageSignalService {
  if (!messageSignalServiceInstance) {
    messageSignalServiceInstance = new MessageSignalService();
  }
  return messageSignalServiceInstance;
}

export default MessageSignalService;
